'use client'

import { useState } from "react";
import { Defense, Log, LogLastEvaluatedKey, User } from "../../lib/definitions";
import { fetchLogs } from "../../lib/data";
import LogTable from "./logTable";
import RatingChart from "./ratingChart";
import SuccessRate from "./successRate";

import styles from "./page.module.css";

type FetchedLogData = {
  rangeLogs: Log[]
  rankLogs: Log[]
  lastEvaluatedKey?: LogLastEvaluatedKey
}

type Props = {
  user: User
  initLogs: Log[]
  logLastEvaluatedKey?: LogLastEvaluatedKey
}
const Detail = ({ user, initLogs, logLastEvaluatedKey }: Props) => {
  const [defense, setDefense] = useState(Defense.RANK);
  const [data, setData] = useState<FetchedLogData>({
    rangeLogs: initLogs.filter((log) => log.defense === Defense.RANGE),
    rankLogs: initLogs.filter((log) => log.defense === Defense.RANK),
    lastEvaluatedKey: logLastEvaluatedKey
  });

  const isProblemSolving = new Date().getTime() - new Date(user.updatedAt).getTime() < 60 * 60 * 1000 + 5 * 60 * 1000;
  const isSameDefenseType = user.status !== "NONE" && user.defense === defense
  const waitingStatusLog = (isProblemSolving && isSameDefenseType) ? {
    id: "waitingStatusLogId",
    handle: user.handle,
    problemId: user.problemId,
    problemName: user.problemName,
    problemLevel: user.problemLevel,
    rating: user.rating,
    defense: user.defense,
    status: user.status,
    elapsedTime: "elapsedTime",
    createdAt: user.updatedAt,
  } : undefined

  const handleFetchMoreLogs = async () => {
    const {
      logs: newLogs,
      lastEvaluatedKey
    } = await fetchLogs(user.handle, logLastEvaluatedKey);

    const newRangeLogs = newLogs.filter((log) => log.defense === Defense.RANGE);
    const newRankLogs = newLogs.filter((log) => log.defense === Defense.RANK);

    setData((data) => ({
      ...data,
      rangeLogs: [...data.rangeLogs, ...newRangeLogs].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }),
      rankLogs: [...data.rankLogs, ...newRankLogs].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }),
      lastEvaluatedKey
    }));
  }

  const handleChangeRank = () => {
    setDefense(Defense.RANK);
  }

  const handleChangeRange = () => {
    setDefense(Defense.RANGE);
  }

  return (
    <div className={styles.userDetail}>
      <RatingChart logs={data.rankLogs} rating={user.rating} />
      <SuccessRate logs={data.rankLogs} rating={user.rating} />
      <div className={styles.defenseTypeContainer}>
        <button
          className={defense === Defense.RANK ? styles.defenseActiveButton : styles.defenseInactiveButton}
          onClick={handleChangeRank}
        >
          Rank
        </button>
        <button
          className={defense === Defense.RANGE ? styles.defenseActiveButton : styles.defenseInactiveButton}
          onClick={handleChangeRange}
        >
          Range
        </button>
      </div>
      <LogTable
        currentLog={waitingStatusLog}
        logs={defense === Defense.RANGE ? data.rangeLogs : data.rankLogs}
        hasRemainingLogs={Boolean(logLastEvaluatedKey)}
        fetchMoreLogs={handleFetchMoreLogs}
      />
    </div>
  );
}

export default Detail